import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { print } from 'graphql';
import {
  AddressInputOrdered,
  clubCodeItem,
  ConfigPaymentClubOrder,
  DetailClubFitting,
  DetailClubFittingItem,
  ListTecFitData,
  SelectedClub,
  ShippingCenterAddress,
} from '../models/clubFitting';
import {
  ApplyCouponToCartInput,
  BillingAddressInput,
  Cart,
  Customer,
  CustomerAddressInput,
  MutationUpdateCustomerAddressArgs,
  RemoveCouponFromCartInput,
  ShippingAddressInput,
} from '../models/shop';
import { SquareData } from '../models/shopCustom';
import {
  APPLY_COUPON_OMNI,
  CONVERT_FULL_CLUB_ORDERS_TO_CART,
  CREATE_CUSTOMER_ADDRESS,
  DELETE_CUSTOMER_ADDRESS,
  GET_CUSTOMER_ORDER_ITEMBY_ORDER_NUMBER,
  REMOVE_COUPON_FROM_CART_OMNI,
  SET_BILLING_ADDRESS_ON_CART_OMNI,
  SET_OMNI_SELECTED_SET_OMNI,
  SET_PAYMENT_METHOD_AND_PLACE_ORDER_CLUB_FITTING,
  SET_SHIPPING_ADDRESSES_ON_CART_OMNI,
  SET_SHIPPING_METHODS_ON_CART_OMNI,
  UPDATE_CUSTOMER_ADDRESS,
} from '../utils/mutations';
import { GET_CUSTOMER_CART_OMNI, GET_CUSTOMER_INFO } from '../utils/queries';
import request from '../utils/request';
import { RootState } from '../utils/store';
import { signOut } from './user';

export const fetchTecfitData = createAsyncThunk(
  'techfit/techfitData',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('/api/v2/tecfit/session');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchTecfitV3Data = createAsyncThunk(
  'techfit/fetchTecfitV3Data',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('/api/v3/tecfit/session');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface TecfitData {
  code: number;
}

interface LoadDetail {
  type: string;
  selected: string;
  created: string;
}

interface LoadDetailV2 {
  type: string;
  selected: string;
  id: string;
}

export const fetchTecfitDetail = createAsyncThunk(
  'techfit/techfitDetail',
  async (payload: LoadDetail, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/tecfit/session/detail?type=${payload.type}&created=${payload.created}&selected=${payload.selected}&renderFittingChart=true`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchTecfitDetailV3 = createAsyncThunk(
  'techfit/fetchTecfitDetailV3',
  async (payload: LoadDetailV2, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/tecfit/session/detail?type=${payload.type}&sessionId=${payload.id}&selected=${payload.selected}&renderFittingChart=true`
      );
      let dataCustom = {
        ...data,
        data: {
          ...data.data,
          selected: payload.selected,
          id: payload.id,
        },
      };
      return dataCustom;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchDetailCustomClubById = createAsyncThunk(
  'techfit/fetchDetailCustomClubById',
  async (payload: { id: number; eStoreCode: string }, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/v3/tecfit/custom-club?id=${payload.id}&storeCode=${payload.eStoreCode}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const convertFullClubOrderToCart = createAsyncThunk(
  'techfit/convertFullClubOrderToCart',
  async (item: DetailClubFitting, { rejectWithValue }) => {
    try {
      const {
        data: {
          data: { convertCustomClubToCart },
        },
      } = await request.post(
        'graphql',
        {
          query: print(CONVERT_FULL_CLUB_ORDERS_TO_CART),
          variables: {
            custom_club_increment_id: item.orderId,
            custom_club_id: item.id,
          },
        },
        {
          params: { storeCodeclubOrders: item.storeCode },
        }
      );
      return convertCustomClubToCart;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getClubOrderCart = createAsyncThunk(
  'techfit/getClubOrderCart',
  async (
    payload: { item: DetailClubFitting; customerToken: string },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: { customerCart },
        },
      } = await request.post(
        'graphql',
        { operationName: 'Cart', query: print(GET_CUSTOMER_CART_OMNI) },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.item.storeCode
              ? payload.item.storeCode
              : payload.item.orderStoreCode,
          },
        }
      );
      return customerCart;
    } catch (e) {
      console.log(e);
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getShippingCenterAddress = createAsyncThunk(
  'techfit/getShippingCenterAddress',
  async (
    payload: {
      storeCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `rest/${payload.storeCode}/V1/omni-channel/center/address`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getSquarePaymentConfig = createAsyncThunk(
  'techfit/getSquarePaymentConfig',
  async (
    payload: {
      orderIncrementId: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `rest/${payload.storeCode}/V1/omni-channel/square-payment/config?order_increment_id=${payload.orderIncrementId}`,
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            ignore_authorization: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAffirmPaymentConfig = createAsyncThunk(
  'techfit/getAffirmPaymentConfig',
  async (
    payload: {
      orderIncrementId: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `rest/${payload.storeCode}/V1/omni-channel/affirm-payment/config?order_increment_id=${payload.orderIncrementId}`,
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            ignore_authorization: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const checkCouponCode = createAsyncThunk(
  'techfit/checkCouponCode',
  async (
    payload: {
      couponCode: string;
      orderIncrementId: string;
      cartId: string;
      storeCode: string;
      amountItems: number;
      subtotal: number;
      subtotal_incl_tax: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `rest/${payload.storeCode}/V1/tecfit/deposit/coupon/check`,
        {
          coupon_code: payload.couponCode,
          order_increment_id: payload.orderIncrementId,
          additional_data: {
            items_total_amount: payload.amountItems,
            subtotal: payload.subtotal,
            subtotal_incl_tax: payload.subtotal_incl_tax,
          },
          mask_id: payload.cartId,
        },
        {
          params: {
            ignore_authorization: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const checkGiftCode = createAsyncThunk(
  'techfit/checkGiftCode',
  async (
    payload: {
      giftCode: string;
      orderIncrementId: string;
      cartId: string;
      storeCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `rest/${payload.storeCode}/V1/tecfit/deposit/gift-code/check`,
        {
          gift_code: payload.giftCode,
          order_increment_id: payload.orderIncrementId,
          mask_id: payload.cartId,
        },
        {
          params: {
            ignore_authorization: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setBillingAddressClubOrderOnCart = createAsyncThunk(
  'techfit/setBillingAddressClubOrderOnCart',
  async (
    payload: {
      billingAddressInput: BillingAddressInput;
      cartId: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_BILLING_ADDRESS_ON_CART_OMNI),
          variables: {
            billingAddress: payload.billingAddressInput,
            cartId: payload.cartId,
          },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setShippingAddressesClubOrderOnCart = createAsyncThunk(
  'techfit/setShippingAddressesClubOrderOnCart',
  async (
    payload: {
      shippingAddressInput: ShippingAddressInput;
      cartId: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_SHIPPING_ADDRESSES_ON_CART_OMNI),
          variables: {
            input: {
              shipping_addresses: [payload.shippingAddressInput],
              cart_id: payload.cartId,
            },
          },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);
export const setShippingMethodsClubOrderOnCart = createAsyncThunk(
  'techfit/setShippingMethodsClubOrderOnCart',
  async (
    payload: {
      cartId: string;
      storeCode: string;
      customerToken: string;
      carrierCode: string;
      methodCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_SHIPPING_METHODS_ON_CART_OMNI),
          variables: {
            input: {
              cart_id: payload.cartId,
              shipping_methods: [
                {
                  carrier_code: payload.carrierCode,
                  method_code: payload.methodCode,
                },
              ],
            },
          },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      // return setShippingMethodsOnCart;
      return data;
    } catch (e) {
      const error = e as AxiosError;
      console.log(error);
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const applyCouponOmni = createAsyncThunk(
  'getClubFitting/applyCouponOmni',
  async (
    payload: {
      input: ApplyCouponToCartInput;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(APPLY_COUPON_OMNI),
          variables: {
            input: payload.input,
          },
        },

        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const removeCouponOmni = createAsyncThunk(
  'getClubFitting/removeCouponOmni',
  async (
    payload: {
      input: RemoveCouponFromCartInput;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(REMOVE_COUPON_FROM_CART_OMNI),
          variables: {
            input: payload.input,
          },
        },

        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setOmniSelectedSet = createAsyncThunk(
  'getClubFitting/setOmniSelectedSet',
  async (
    payload: {
      cartId: string;
      omniSelectedSet: number[][];
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_OMNI_SELECTED_SET_OMNI),
          variables: {
            cartId: payload.cartId,
            omniSelectedSet: JSON.stringify(payload.omniSelectedSet),
          },
        },

        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteOrderUnpaid = createAsyncThunk(
  'getClubFitting/deleteOrderUnpaid',
  async (
    payload: {
      orderNumber: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/tecfit/payment/delete-order`,
        { order_increment_id: payload.orderNumber },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setPaymentMethodAndPlaceOrderClubOrder = createAsyncThunk(
  'getClubFitting/setPaymentMethodAndPlaceOrderClubOrder',
  async (
    payload: {
      cart_id: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(SET_PAYMENT_METHOD_AND_PLACE_ORDER_CLUB_FITTING),
          variables: {
            cartId: payload.cart_id,
            input: {
              cart_id: payload.cart_id,
              payment_method: {
                code: 'checkmo',
              },
            },
          },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getCustomerOrderItemByOrderNumber = createAsyncThunk(
  'getClubFitting/getCustomerOrderItemByOrderNumber',
  async (
    payload: {
      orderNumber: string;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(GET_CUSTOMER_ORDER_ITEMBY_ORDER_NUMBER),
          variables: {
            orderNumber: payload.orderNumber,
          },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setDeposit = createAsyncThunk(
  'getClubFitting/setDeposit',
  async (
    payload: {
      order_increment_id: string;
      set_information: { order_item_ids: number[] }[];
      deposit_request: {
        amount: number;
        card_nonce: string;
        cc_number: string;
        card_type: string;
        location_id: string;
      };
      giftvoucher_deposit_used: { gift_code: string; amount: number }[];
      coupon_used?: {
        coupon_code: string;
        amount: number;
        additional_data: {
          subtotal: number;
          subtotal_incl_tax: number;
        };
      };
      storeCode: string;
      customerToken: string;
      ignore_authorization: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      let params = {};
      if (payload.coupon_used && payload.coupon_used.amount > 0) {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          deposit_request: payload.deposit_request,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
          coupon_used: payload.coupon_used,
        };
      } else {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          deposit_request: payload.deposit_request,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
        };
      }
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/tecfit/payment/deposit-take-payment`,
        params,
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            ignore_authorization: payload.ignore_authorization,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setZeroDeposit = createAsyncThunk(
  'getClubFitting/setZeroDeposit',
  async (
    payload: {
      order_increment_id: string;
      set_information: { order_item_ids: number[] }[];
      giftvoucher_deposit_used: { gift_code: string; amount: number }[];
      coupon_used?: {
        coupon_code: string;
        amount: number;
        additional_data: {
          subtotal: number;
          subtotal_incl_tax: number;
        };
      };
      storeCode: string;
      customerToken: string;
      ignore_authorization: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      let params = {};
      if (payload.coupon_used && payload.coupon_used.amount > 0) {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
          coupon_used: payload.coupon_used,
        };
      } else {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
        };
      }
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/tecfit/payment/zero-deposit-take-payment`,
        params,
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            ignore_authorization: payload.ignore_authorization,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const setDepositAffirm = createAsyncThunk(
  'getClubFitting/setDepositAffirm',
  async (
    payload: {
      order_increment_id: string;
      set_information: { order_item_ids: number[] }[];
      deposit_request: {
        amount: number;
        checkout_token: string;
      };
      giftvoucher_deposit_used: { gift_code: string; amount: number }[];
      coupon_used?: {
        coupon_code: string;
        amount: number;
        additional_data: {
          subtotal: number;
          subtotal_incl_tax: number;
        };
      };
      storeCode: string;
      customerToken: string;
      ignore_authorization: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      let params = {};
      if (payload.coupon_used && payload.coupon_used.amount > 0) {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          deposit_request: payload.deposit_request,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
          coupon_used: payload.coupon_used,
        };
      } else {
        params = {
          order_increment_id: payload.order_increment_id,
          set_information: payload.set_information,
          deposit_request: payload.deposit_request,
          giftvoucher_deposit_used: payload.giftvoucher_deposit_used,
        };
      }
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/tecfit/payment/affirm/deposit-take-payment`,
        params,
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            ignore_authorization: payload.ignore_authorization,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getCustomerClubOrder = createAsyncThunk(
  'getClubFitting/getCustomerClubOrder',
  async (
    payload: { storeCode: string; customerToken: string },
    { rejectWithValue }
  ) => {
    try {
      const {
        data: {
          data: { customer },
        },
      } = await request.post(
        'graphql',
        { query: print(GET_CUSTOMER_INFO) },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return customer;
    } catch (e) {
      console.log(e);
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createCustomerClubOrderAddress = createAsyncThunk(
  'getClubFitting/createCustomerClubOrderAddress',
  async (
    payload: {
      storeCode: string;
      customerToken: string;
      customerAddressInput: CustomerAddressInput;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(CREATE_CUSTOMER_ADDRESS),
          variables: { customerAddressInput: payload.customerAddressInput },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      if (data.errors) {
        return rejectWithValue(data.errors);
      }

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateCustomerClubOrderAddress = createAsyncThunk(
  'getClubFitting/updateCustomerClubOrderAddress',
  async (
    payload: {
      update: MutationUpdateCustomerAddressArgs;
      storeCode: string;
      customerToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(UPDATE_CUSTOMER_ADDRESS),
          variables: { id: payload.update.id, input: payload.update.input },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteCustomerClubOrderAddress = createAsyncThunk(
  'getClubFitting/deleteCustomerClubOrderAddress',
  async (
    payload: { id: number; storeCode: string; customerToken: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        'graphql',
        {
          query: print(DELETE_CUSTOMER_ADDRESS),
          variables: { id: payload.id },
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
          },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const generateTokenCustomer = createAsyncThunk(
  'getClubFitting/generateTokenCustomer',
  async (
    payload: {
      storeCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `/api/ecommerce/token?storeCode=${payload.storeCode}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const editShippingOrdered = createAsyncThunk(
  'getClubFitting/editShippingOrdered',
  async (
    payload: {
      storeCode: string;
      customerToken: string;
      incrementId: string;
      orderShippingAddress: AddressInputOrdered;
      shipping_description: string;
      is_same_address: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `rest/${payload.storeCode}/V1/tecfit/omni/custom-club/edit-shipping`,
        {
          increment_id: payload.incrementId,
          order_shipping_address: payload.orderShippingAddress,
          shipping_description: payload.shipping_description,
          is_same_address: payload.is_same_address,
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            isShopGolftec: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const editBillingOrdered = createAsyncThunk(
  'getClubFitting/editBillingOrdered',
  async (
    payload: {
      storeCode: string;
      customerToken: string;
      incrementId: string;
      orderBillingAddress: AddressInputOrdered;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `rest/${payload.storeCode}/V1/tecfit/omni/custom-club/edit-billing`,
        {
          increment_id: payload.incrementId,
          order_billing_address: payload.orderBillingAddress,
        },
        {
          params: {
            customerTokenClubOrders: payload.customerToken,
            storeCodeclubOrders: payload.storeCode,
            isShopGolftec: true,
          },
        }
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface TecfitState {
  loadingData: boolean;
  error: null | number;
  code: null | TecfitData;
  selectedClubFitting: null | DetailClubFittingItem;
  selectedQuickView: null | DetailClubFittingItem;
  dataClubFitting: null | DetailClubFitting[];
  loadingDetail: boolean;
  detailClubFitting: null | SelectedClub;
  isRedirect: boolean;
  statusOpenQuickViewClubFitting: boolean;
  listTecfitData: ListTecFitData | null;
  detailClubFittingItemV3: SelectedClub[];
  loadingDetailV3: boolean;
  clubOrderCart: Cart | null;
  loadingShippingCenterAddress: boolean;
  shippingCenterAddress: ShippingCenterAddress | null;
  giftCodeUsage: clubCodeItem[];
  couponCodeUsage: clubCodeItem[];
  customerToken: string;
  openPaymentClubOrder: boolean;
  stepModalPayment:
    | 'select'
    | 'payment'
    | 'payment-due'
    | 'error-order'
    | 'error-payment'
    | 'error-select'
    | 'error-address'
    | 'error-paymented'
    | 'success'
    | 'error-order-mobile'
    | 'error-order-mobile-payment'
    | 'error-item-unvailable';
  messageError: string;
  orderIncrementIdSuccess: string | null;
  paymentItemSuccess: DetailClubFitting | null;
  configSquareClubOrder: ConfigPaymentClubOrder | null;
  configAffirmClubOrder: ConfigPaymentClubOrder | null;
  loadingSetBillingClubOrderCart: boolean;
  loadingSetShippingClubOrderCart: boolean;
  loadingSetShippingMethodClubOrderCart: boolean;
  loadingAddRemoveCoupon: boolean;
  listItemIdPaymentMobile: number[];
  customerInfoClubOrder: Customer | null;
  methodPaymentMobile: string;
  dataSquareCardMobile: SquareData | null;
  findClubOrder: DetailClubFitting | null;
  loadingSetOmniSelectSet: boolean;
}

const initialState: TecfitState = {
  loadingData: false,
  error: null,
  code: null,
  selectedClubFitting: null,
  selectedQuickView: null,
  dataClubFitting: null,
  loadingDetail: false,
  detailClubFitting: null,
  isRedirect: false,
  statusOpenQuickViewClubFitting: false,
  listTecfitData: null,
  detailClubFittingItemV3: [],
  loadingDetailV3: false,
  clubOrderCart: null,
  loadingShippingCenterAddress: false,
  shippingCenterAddress: null,
  giftCodeUsage: [],
  couponCodeUsage: [],
  customerToken: '',
  openPaymentClubOrder: false,
  stepModalPayment: 'error-order-mobile',
  messageError: '',
  orderIncrementIdSuccess: null,
  paymentItemSuccess: null,
  configSquareClubOrder: null,
  configAffirmClubOrder: null,
  loadingSetBillingClubOrderCart: false,
  loadingSetShippingClubOrderCart: false,
  loadingSetShippingMethodClubOrderCart: false,
  loadingAddRemoveCoupon: false,
  listItemIdPaymentMobile: [],
  customerInfoClubOrder: null,
  methodPaymentMobile: '',
  dataSquareCardMobile: null,
  findClubOrder: null,
  loadingSetOmniSelectSet: false,
};

export const tecfitSlice = createSlice({
  name: 'techfit',
  initialState,
  reducers: {
    changeSelectClubFitting: (state, action) => {
      state.selectedClubFitting = action.payload;
    },
    changeSelectClubFittingQuickView: (state, action) => {
      state.selectedQuickView = action.payload;
    },
    changeClubFittingStatusRedirect: (state, action) => {
      state.isRedirect = action.payload;
    },
    changeStatusQuickViewClubFitting: (state, action) => {
      state.statusOpenQuickViewClubFitting = action.payload;
    },
    addGiftCode: (state, action) => {
      state.giftCodeUsage.push(action.payload);
    },
    changeGiftCode: (state, action) => {
      state.giftCodeUsage = action.payload;
    },
    addCouponCode: (state, action) => {
      state.couponCodeUsage = action.payload;
    },
    clearCouponGiftCode: (state) => {
      state.couponCodeUsage = [];
      state.giftCodeUsage = [];
    },

    removeClubDiscountCode: (state, action) => {
      switch (action.payload.type) {
        case 'coupon':
          state.couponCodeUsage = state.couponCodeUsage.filter(
            (item) => item.code !== action.payload.code
          );
          break;
        case 'gift':
          state.giftCodeUsage = state.giftCodeUsage.filter(
            (item) => item.code !== action.payload.code
          );
          break;
      }
    },
    setCustomerToken: (state, action) => {
      state.customerToken = action.payload;
    },
    changeOpenPaymentClubFitting: (state, action) => {
      state.openPaymentClubOrder = action.payload;
    },
    changeStepPaymentClubFitting: (state, action) => {
      state.stepModalPayment = action.payload;
    },
    changeMessageErrorClubFitting: (state, action) => {
      state.messageError = action.payload;
    },
    changeOrderIncrementIdSuccess: (state, action) => {
      state.orderIncrementIdSuccess = action.payload;
    },
    changePaymentItemSuccess: (state, action) => {
      state.paymentItemSuccess = action.payload;
    },
    removeCustomClubById: (state, action) => {
      if (state.listTecfitData && state.listTecfitData.listCustomClubOrder) {
        state.listTecfitData.listCustomClubOrder =
          state.listTecfitData.listCustomClubOrder.filter(
            (i) => i.id !== action.payload.id
          );
      }
    },
    changeListItemIdPaymentMobile: (state, action) => {
      state.listItemIdPaymentMobile = action.payload;
    },
    changePaymentMethodMobile: (state, action) => {
      state.methodPaymentMobile = action.payload;
    },
    changeDataSquareCardMobile: (state, action) => {
      state.dataSquareCardMobile = action.payload;
    },
    changeFindClubOrder: (state, action) => {
      state.findClubOrder = action.payload;
    },
    clearConfigClubOrderCart: (state) => {
      state.clubOrderCart = null;
      state.findClubOrder = null;
      state.methodPaymentMobile = '';
      state.dataSquareCardMobile = null;
      state.listItemIdPaymentMobile = [];
      state.customerInfoClubOrder = null;
      state.giftCodeUsage = [];
      state.couponCodeUsage = [];
      state.customerToken = '';
      state.stepModalPayment = 'select';
      state.messageError = '';
      state.orderIncrementIdSuccess = null;
      state.paymentItemSuccess = null;
      state.configSquareClubOrder = null;
      state.configAffirmClubOrder = null;
      state.loadingSetBillingClubOrderCart = false;
      state.loadingSetShippingClubOrderCart = false;
      state.loadingSetShippingMethodClubOrderCart = false;
      state.loadingSetOmniSelectSet = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTecfitData.pending, (state, action) => {
        // state.loadingData = true;
      })

      .addCase(fetchTecfitData.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.dataClubFitting = data;
        state.code = action.payload;
        // state.loadingData = false;
      })
      .addCase(fetchTecfitV3Data.pending, (state, action) => {
        state.loadingData = true;
      })
      .addCase(fetchTecfitV3Data.fulfilled, (state, action) => {
        state.listTecfitData = action.payload.data;
        if (action.payload) {
          state.loadingData = false;
        }
      })
      .addCase(fetchTecfitV3Data.rejected, (state, action) => {
        state.listTecfitData = {
          listTecfitSession: [],
          listCustomClubOrder: [],
        };
        state.loadingData = false;
      })
      .addCase(fetchTecfitDetail.pending, (state, action) => {
        state.loadingDetail = true;
      })
      .addCase(fetchTecfitDetail.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.detailClubFitting = data;
        state.loadingDetail = false;
      })
      .addCase(fetchTecfitDetailV3.pending, (state, action) => {
        state.loadingDetailV3 = true;
      })
      .addCase(fetchTecfitDetailV3.fulfilled, (state, action) => {
        const { data } = action.payload;
        const idx = state.detailClubFittingItemV3.findIndex(
          (i) =>
            i.selected === data.selected &&
            i.id === data.id &&
            i.type === data.type
        );
        if (idx >= 0) {
          state.detailClubFittingItemV3[idx] = data;
        } else {
          state.detailClubFittingItemV3.push(data);
        }
        state.loadingDetailV3 = false;
      })
      .addCase(getClubOrderCart.fulfilled, (state, action) => {
        state.clubOrderCart = action.payload;
      })
      .addCase(setShippingAddressesClubOrderOnCart.pending, (state) => {
        state.loadingSetShippingClubOrderCart = true;
      })
      .addCase(setBillingAddressClubOrderOnCart.pending, (state) => {
        state.loadingSetBillingClubOrderCart = true;
      })
      .addCase(setShippingMethodsClubOrderOnCart.pending, (state) => {
        state.loadingSetShippingMethodClubOrderCart = true;
      })
      .addCase(
        setShippingAddressesClubOrderOnCart.fulfilled,
        (state, action) => {
          if (action.payload?.data?.setShippingAddressesOnCart?.cart) {
            state.clubOrderCart =
              action.payload?.data?.setShippingAddressesOnCart?.cart;
          } else if (
            action.payload?.errors &&
            action.payload?.errors.length > 0
          ) {
            if (
              action.payload?.errors[0]?.extensions &&
              action.payload?.errors[0]?.extensions?.error_code &&
              [1002, 1003, 1004, 1005, 1006].includes(
                action.payload?.errors[0]?.extensions?.error_code
              )
            ) {
              if (
                [1003, 1004, 1005, 1006].includes(
                  action.payload?.errors[0]?.extensions?.error_code
                )
              ) {
                state.stepModalPayment = 'error-paymented';
              } else {
                state.stepModalPayment = 'error-order';
                if (action.payload?.errors[0]?.extensions?.custom_club_id) {
                  if (
                    state.listTecfitData &&
                    state.listTecfitData.listCustomClubOrder
                  ) {
                    state.listTecfitData.listCustomClubOrder =
                      state.listTecfitData.listCustomClubOrder.filter(
                        (i) =>
                          i.id !==
                          parseInt(
                            action.payload?.errors[0]?.extensions
                              ?.custom_club_id
                          )
                      );
                  }
                }
              }
            } else {
              state.stepModalPayment = 'error-address';
            }
            state.messageError = action.payload?.errors[0].message || '';
          }
          state.loadingSetShippingClubOrderCart = false;
        }
      )
      .addCase(setBillingAddressClubOrderOnCart.fulfilled, (state, action) => {
        if (action.payload?.data?.setBillingAddressOnCart?.cart) {
          state.clubOrderCart =
            action.payload?.data?.setBillingAddressOnCart?.cart;
        } else if (
          action.payload?.errors &&
          action.payload?.errors.length > 0
        ) {
          if (
            action.payload?.errors[0]?.extensions &&
            action.payload?.errors[0]?.extensions?.error_code &&
            [1002, 1003, 1004, 1005, 1006].includes(
              action.payload?.errors[0]?.extensions?.error_code
            )
          ) {
            if (
              [1003, 1004, 1005, 1006].includes(
                action.payload?.errors[0]?.extensions?.error_code
              )
            ) {
              state.stepModalPayment = 'error-paymented';
            } else {
              state.stepModalPayment = 'error-order';
              if (action.payload?.errors[0]?.extensions?.custom_club_id) {
                if (
                  state.listTecfitData &&
                  state.listTecfitData.listCustomClubOrder
                ) {
                  state.listTecfitData.listCustomClubOrder =
                    state.listTecfitData.listCustomClubOrder.filter(
                      (i) =>
                        i.id !==
                        parseInt(
                          action.payload?.errors[0]?.extensions?.custom_club_id
                        )
                    );
                }
              }
            }
          } else {
            state.stepModalPayment = 'error-address';
          }
          state.messageError = action.payload?.errors[0].message || '';
        }
        state.loadingSetBillingClubOrderCart = false;
      })
      .addCase(setShippingMethodsClubOrderOnCart.fulfilled, (state, action) => {
        if (action.payload?.data?.setShippingMethodsOnCart?.cart) {
          state.clubOrderCart =
            action.payload?.data?.setShippingMethodsOnCart?.cart;
        } else if (
          action.payload?.errors &&
          action.payload?.errors.length > 0
        ) {
          if (
            action.payload?.errors[0]?.extensions &&
            action.payload?.errors[0]?.extensions?.error_code &&
            [1002, 1003, 1004, 1005, 1006].includes(
              action.payload?.errors[0]?.extensions?.error_code
            )
          ) {
            if (
              [1003, 1004, 1005, 1006].includes(
                action.payload?.errors[0]?.extensions?.error_code
              )
            ) {
              state.stepModalPayment = 'error-paymented';
            } else {
              state.stepModalPayment = 'error-order';
              if (action.payload?.errors[0]?.extensions?.custom_club_id) {
                if (
                  state.listTecfitData &&
                  state.listTecfitData.listCustomClubOrder
                ) {
                  state.listTecfitData.listCustomClubOrder =
                    state.listTecfitData.listCustomClubOrder.filter(
                      (i) =>
                        i.id !==
                        parseInt(
                          action.payload?.errors[0]?.extensions?.custom_club_id
                        )
                    );
                }
              }
            }
          } else {
            state.stepModalPayment = 'error-address';
          }
          state.messageError = action.payload?.errors[0].message || '';
        }
        state.loadingSetShippingMethodClubOrderCart = false;
      })
      .addCase(applyCouponOmni.pending, (state) => {
        state.loadingAddRemoveCoupon = true;
      })
      .addCase(applyCouponOmni.fulfilled, (state, action) => {
        if (action.payload?.data?.applyCouponToCart?.cart) {
          state.clubOrderCart = action.payload?.data?.applyCouponToCart?.cart;
        }

        state.loadingAddRemoveCoupon = false;
      })
      .addCase(removeCouponOmni.pending, (state) => {
        state.loadingAddRemoveCoupon = true;
      })
      .addCase(removeCouponOmni.fulfilled, (state, action) => {
        if (action.payload?.data?.removeCouponFromCart?.cart) {
          state.clubOrderCart =
            action.payload?.data?.removeCouponFromCart?.cart;
        } else if (
          action.payload?.errors &&
          action.payload?.errors.length > 0
        ) {
          if (
            action.payload?.errors[0]?.extensions &&
            action.payload?.errors[0]?.extensions?.error_code &&
            [1002, 1003, 1004, 1005, 1006].includes(
              action.payload?.errors[0]?.extensions?.error_code
            )
          ) {
            if (
              [1003, 1004, 1005, 1006].includes(
                action.payload?.errors[0]?.extensions?.error_code
              )
            ) {
              state.stepModalPayment = 'error-paymented';
            } else {
              state.stepModalPayment = 'error-order';
              if (action.payload?.errors[0]?.extensions?.custom_club_id) {
                if (
                  state.listTecfitData &&
                  state.listTecfitData.listCustomClubOrder
                ) {
                  state.listTecfitData.listCustomClubOrder =
                    state.listTecfitData.listCustomClubOrder.filter(
                      (i) =>
                        i.id !==
                        parseInt(
                          action.payload?.errors[0]?.extensions?.custom_club_id
                        )
                    );
                }
              }
            }
          } else {
            state.stepModalPayment = 'error-address';
          }
          state.messageError = action.payload?.errors[0].message || '';
        }
        state.loadingAddRemoveCoupon = false;
      })
      .addCase(getShippingCenterAddress.pending, (state) => {
        state.loadingShippingCenterAddress = true;
      })
      .addCase(getShippingCenterAddress.fulfilled, (state, action) => {
        state.shippingCenterAddress = action.payload;
        state.loadingShippingCenterAddress = false;
      })
      .addCase(fetchDetailCustomClubById.fulfilled, (state, action) => {
        const { data } = action.payload;
        if (data.customClubItems && data.customClubItems.length > 0) {
          if (
            state.listTecfitData &&
            state.listTecfitData.listCustomClubOrder
          ) {
            const idex = state.listTecfitData.listCustomClubOrder.findIndex(
              (i) => i.id === data.id
            );
            if (idex >= 0) {
              const existingItem =
                state.listTecfitData.listCustomClubOrder[idex];
              const newItem = { ...data };

              if (
                Array.isArray(existingItem.customClubItems) &&
                Array.isArray(newItem.customClubItems)
              ) {
                const existingMap = Object.fromEntries(
                  existingItem.customClubItems.map((item) => [
                    item.groupId,
                    item,
                  ])
                );
                newItem.customClubItems = newItem.customClubItems.map(
                  (newClubItem) => {
                    const oldClubItem = existingMap[newClubItem.groupId] || {};
                    return {
                      ...newClubItem,
                      sessionId:
                        oldClubItem.sessionId ?? newClubItem.sessionId ?? '',
                      type: oldClubItem.type ?? newClubItem.type ?? '',
                      selected:
                        oldClubItem.selected ?? newClubItem.selected ?? '',
                      isTecfitSession:
                        oldClubItem.isTecfitSession ??
                        newClubItem.isTecfitSession ??
                        false,
                    };
                  }
                );
              }
              state.listTecfitData.listCustomClubOrder[idex] = newItem;
            } else {
              state.listTecfitData.listCustomClubOrder.push(data);
            }
          }
        } else {
          if (
            state.listTecfitData &&
            state.listTecfitData.listCustomClubOrder
          ) {
            state.listTecfitData.listCustomClubOrder =
              state.listTecfitData.listCustomClubOrder.filter(
                (i) => data.id && i.id !== data.id
              );
          }
        }
      })
      .addCase(getSquarePaymentConfig.fulfilled, (state, action) => {
        state.configSquareClubOrder = action.payload;
      })
      .addCase(getAffirmPaymentConfig.fulfilled, (state, action) => {
        state.configAffirmClubOrder = action.payload;
      })
      .addCase(getCustomerClubOrder.fulfilled, (state, action) => {
        if (action.payload.addresses) {
          state.customerInfoClubOrder = action.payload;
        }
      })
      .addCase(setOmniSelectedSet.pending, (state) => {
        state.loadingSetOmniSelectSet = true;
      })
      .addCase(setOmniSelectedSet.fulfilled, (state, action) => {
        if (action.payload?.data?.setOmniSelectedSet?.cart) {
          state.clubOrderCart = action.payload?.data?.setOmniSelectedSet?.cart;
        } else if (
          action.payload?.errors &&
          action.payload?.errors.length > 0
        ) {
          if (
            action.payload?.errors[0]?.extensions &&
            action.payload?.errors[0]?.extensions?.error_code &&
            [1002, 1003, 1004, 1005, 1006].includes(
              action.payload?.errors[0]?.extensions?.error_code
            )
          ) {
            if (
              [1003, 1004, 1005, 1006].includes(
                action.payload?.errors[0]?.extensions?.error_code
              )
            ) {
              state.stepModalPayment = 'error-paymented';
            } else {
              state.stepModalPayment = 'error-order';
              if (action.payload?.errors[0]?.extensions?.custom_club_id) {
                if (
                  state.listTecfitData &&
                  state.listTecfitData.listCustomClubOrder
                ) {
                  state.listTecfitData.listCustomClubOrder =
                    state.listTecfitData.listCustomClubOrder.filter(
                      (i) =>
                        i.id !==
                        parseInt(
                          action.payload?.errors[0]?.extensions?.custom_club_id
                        )
                    );
                }
              }
            }
          } else {
            state.stepModalPayment = 'error-address';
          }
          state.messageError = action.payload?.errors[0].message || '';
        }
        state.loadingSetOmniSelectSet = false;
      })
      .addCase(editBillingOrdered.pending, (state) => {
        state.loadingSetBillingClubOrderCart = true;
      })
      .addCase(editBillingOrdered.fulfilled, (state) => {
        state.loadingSetBillingClubOrderCart = false;
      })
      .addCase(editShippingOrdered.pending, (state) => {
        state.loadingSetShippingClubOrderCart = true;
      })
      .addCase(editShippingOrdered.fulfilled, (state) => {
        state.loadingSetShippingClubOrderCart = false;
      })
      .addCase(signOut, () => ({ ...initialState }))
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => {
          state.error = action.payload;
          state.loadingDetailV3 = false;
          state.loadingSetShippingClubOrderCart = false;
          state.loadingSetBillingClubOrderCart = false;
          state.loadingSetShippingMethodClubOrderCart = false;
        }
      );
  },
});

export const {
  changeStatusQuickViewClubFitting,
  changeSelectClubFitting,
  changeSelectClubFittingQuickView,
  changeClubFittingStatusRedirect,
  clearConfigClubOrderCart,
  addGiftCode,
  addCouponCode,
  removeClubDiscountCode,
  setCustomerToken,
  changeOpenPaymentClubFitting,
  changeStepPaymentClubFitting,
  changeMessageErrorClubFitting,
  changeOrderIncrementIdSuccess,
  changePaymentItemSuccess,
  removeCustomClubById,
  changeGiftCode,
  clearCouponGiftCode,
  changeListItemIdPaymentMobile,
  changePaymentMethodMobile,
  changeDataSquareCardMobile,
  changeFindClubOrder,
} = tecfitSlice.actions;

export const selectSampleTecfit = (state: RootState) =>
  state.techfit.code as TecfitData;
export const loadingTecfitData = (state: RootState) =>
  state.techfit.loadingData;
export const selectClubFitting = (state: RootState) =>
  state.techfit.selectedClubFitting;
export const selectClubFittingQuickView = (state: RootState) =>
  state.techfit.selectedQuickView;
export const selectClubFittingData = (state: RootState) =>
  state.techfit.dataClubFitting;
export const loadingTecfitDetail = (state: RootState) =>
  state.techfit.loadingDetail;
export const selectClubFittingDetail = (state: RootState) =>
  state.techfit.detailClubFitting;
export const selectClubFittingStatusRedirect = (state: RootState) =>
  state.techfit.isRedirect;
export const selectStatusQuickViewClubFitting = (state: RootState) =>
  state.techfit.statusOpenQuickViewClubFitting;
export const selectListTecfitData = (state: RootState) =>
  state.techfit.listTecfitData;
export const selectListDetailClubFittingItemV3 = (state: RootState) =>
  state.techfit.detailClubFittingItemV3;
export const selectLoadingListDetailClubFittingItemV3 = (state: RootState) =>
  state.techfit.loadingDetailV3;
export const selectClubOrderCart = (state: RootState) =>
  state.techfit.clubOrderCart;
export const selectShippingCenterAddress = (state: RootState) =>
  state.techfit.shippingCenterAddress;
export const selectGiftCodeUsage = (state: RootState) =>
  state.techfit.giftCodeUsage;
export const selectCouponCodeUsage = (state: RootState) =>
  state.techfit.couponCodeUsage;
export const selectCustomerToken = (state: RootState) =>
  state.techfit.customerToken;
export const selectOpenPaymentClubFitting = (state: RootState) =>
  state.techfit.openPaymentClubOrder;
export const selectStepPaymentClubFitting = (state: RootState) =>
  state.techfit.stepModalPayment;
export const selectMessageErrorClubFitting = (state: RootState) =>
  state.techfit.messageError;
export const selectOrderIncrementIdSuccess = (state: RootState) =>
  state.techfit.orderIncrementIdSuccess;
export const selectPaymentItemSuccess = (state: RootState) =>
  state.techfit.paymentItemSuccess;

export const selectConfigSquareClubOrder = (state: RootState) =>
  state.techfit.configSquareClubOrder;
export const selectConfigAffirmClubOrder = (state: RootState) =>
  state.techfit.configAffirmClubOrder;
export const selectLoadingSetShippingClubOrder = (state: RootState) =>
  state.techfit.loadingSetShippingClubOrderCart;
export const selectLoadingSetBillingClubOrder = (state: RootState) =>
  state.techfit.loadingSetBillingClubOrderCart;
export const selectLoadingSetShippingMethodClubOrder = (state: RootState) =>
  state.techfit.loadingSetShippingMethodClubOrderCart;
export const selectLoadingAddRemoveCoupon = (state: RootState) =>
  state.techfit.loadingAddRemoveCoupon;
export const selectListItemIdPaymentMobile = (state: RootState) =>
  state.techfit.listItemIdPaymentMobile;
export const selectCustomerInfoClubOrder = (state: RootState) =>
  state.techfit.customerInfoClubOrder;
export const selectPaymentMethodMobile = (state: RootState) =>
  state.techfit.methodPaymentMobile;
export const selectDataSquareMobile = (state: RootState) =>
  state.techfit.dataSquareCardMobile;
export const selectOrderPaymentMobile = (state: RootState) =>
  state.techfit.findClubOrder;
export const selectLoadingOmniSelectSet = (state: RootState) =>
  state.techfit.loadingSetOmniSelectSet;
export default tecfitSlice.reducer;
